/**
 * MobileRefreshButton Container
 *
 * Container component for the mobile refresh button that calculates derived values
 * for rendering the refresh button.
 * @remarks
 * This component is simple and primarily passes through props, but follows the
 * Container/View pattern for consistency with other components.
 */

import { useMemo } from "react";

import MobileRefreshButtonView from "./MobileRefreshButtonView";

import type { IconSize } from "@/components/atoms/Icon";

/**
 * Props interface for MobileRefreshButton container component
 */
interface MobileRefreshButtonContainerProps {
  isGenerating: boolean;
  onRefresh: () => void;
  isDark: boolean;
  isSmallScreen: boolean;
}

/**
 * Container component for mobile refresh button
 * @param props - Component props
 * @param props.isGenerating - Whether AI summary is currently being generated
 * @param props.onRefresh - Callback to refresh/regenerate summary
 * @param props.isDark - Whether dark theme is active
 * @param props.isSmallScreen - Whether the screen is small
 */
function MobileRefreshButtonContainer({
  isGenerating,
  onRefresh,
  isDark,
  isSmallScreen,
}: MobileRefreshButtonContainerProps) {
  const iconSize = useMemo<IconSize>(
    () => (isSmallScreen ? "md" : "lg"),
    [isSmallScreen]
  );
  const iconColor = useMemo(() => (isDark ? "#D1D5DB" : "#4B5563"), [isDark]);
  const containerOpacity = useMemo(
    () => (isGenerating ? 0.5 : 1),
    [isGenerating]
  );

  return (
    <MobileRefreshButtonView
      isGenerating={isGenerating}
      onRefresh={onRefresh}
      iconSize={iconSize}
      iconColor={iconColor}
      containerOpacity={containerOpacity}
    />
  );
}

export default MobileRefreshButtonContainer;
